import React, {useEffect, useMemo, useState} from 'react';
import {createPortal} from 'react-dom';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {CaretDown, CaretRight, Check, X} from '@phosphor-icons/react';
import {useHelpDrawer} from './HelpDrawerContext';
import {
    STEP_CONNECT_DATA_SOURCES,
    STEP_DATA_EXPLORER_REPORT,
    STEP_FIRST_ALERT,
    STEP_FIRST_CONTROL_LINE,
    STEP_SELECT_DASHBOARD,
    useOnboardingProgress,
} from './useOnboardingProgress';
import controlLineOnboardingGif from '../../images/onboarding/control-line-onboarding.gif';
import './HelpDrawer.scss';

const POWER_UPS_TOTAL = 10;
const POWER_UPS_ACTIVE = 1;

const STEP_ACTIONS = {
    [STEP_CONNECT_DATA_SOURCES]: {
        labelKey: 'help.action_connect_sources',
        path: '/agency/conections',
    },
    [STEP_FIRST_CONTROL_LINE]: {
        labelKey: 'help.action_create_control_line',
        path: '/agency/clients/new/onboarding',
    },
    [STEP_SELECT_DASHBOARD]: {
        labelKey: 'help.action_select_dashboard',
        path: '/marketing',
    },
    [STEP_FIRST_ALERT]: {
        labelKey: 'help.action_create_alert',
        path: '/agency/alerts-configuration',
    },
    [STEP_DATA_EXPLORER_REPORT]: {
        labelKey: 'help.action_open_data_explorer',
        path: '/data-explorer',
    },
};

const POWER_UP_LABELS = {
    [STEP_CONNECT_DATA_SOURCES]: ['help.powerup_connect_quality'],
    [STEP_SELECT_DASHBOARD]: [
        'help.powerup_dashboard_columns',
        'help.powerup_dashboard_labels',
        'help.powerup_dashboard_views',
    ],
    [STEP_DATA_EXPLORER_REPORT]: ['help.powerup_data_explorer_export'],
};

function PowerUpsIndicator({active, total}) {
    const {t} = useTranslation();

    if (!total) return null;

    return (
        <span className="help-powerups-indicator">
            <span className="help-lightning-icon" aria-hidden="true"/>
            <span>{t('help.powerups_activated_short', {active, total})}</span>
        </span>
    );
}

function PowerUpsDropdown({step}) {
    const {t} = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const labels = POWER_UP_LABELS[step.key] || [];

    if (!step.powerUpsTotal) return null;

    return (
        <div className="help-roadmap-powerups">
            <button
                type="button"
                className="help-powerups-button"
                onClick={() => setIsOpen(prev => !prev)}
                aria-expanded={isOpen}
            >
                <span className="help-lightning-icon" aria-hidden="true"/>
                <span>{t('help.view_section_powerups', {active: step.powerUpsActive, total: step.powerUpsTotal})}</span>
                <CaretDown size={14} weight="bold"/>
            </button>
            {isOpen && (
                <ul className="help-powerups-list">
                    {Array.from({length: step.powerUpsTotal}).map((_, index) => {
                        const isActive = index < step.powerUpsActive;
                        return (
                            <li key={index} className={isActive ? '-active' : ''}>
                                <span className="help-powerups-list__status">
                                    {isActive && <Check size={11} weight="bold"/>}
                                </span>
                                <span>{t(labels[index] || 'help.powerup_generic')}</span>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

function StepActionButton({step, onNavigate}) {
    const {t} = useTranslation();
    const action = STEP_ACTIONS[step.key];

    if (!action) return null;

    return (
        <button
            type="button"
            className="help-roadmap-action-button"
            onClick={() => onNavigate(action.path)}
        >
            {t(action.labelKey)}
        </button>
    );
}

function StepGif({step}) {
    const {t} = useTranslation();

    return (
        <div className="help-roadmap-gif">
            <img src={controlLineOnboardingGif} alt={t(step.labelKey)} />
        </div>
    );
}

function StepDoneAction({step, toggleManualStep}) {
    const {t} = useTranslation();

    return (
        <div className="help-roadmap-done-actions">
            {step.completed && (
                <span className="help-roadmap-done">
                    <Check size={11} weight="bold"/>
                    {t('help.done')}
                </span>
            )}
            <button
                type="button"
                className="help-roadmap-unmark"
                onClick={() => toggleManualStep(step.key)}
            >
                {step.completed ? t('help.unmark') : t('help.mark_as_done')}
            </button>
        </div>
    );
}

function StepDetail({step, toggleManualStep, onNavigate}) {
    const {t} = useTranslation();

    return (
        <div className="help-roadmap-detail">
            <p>{t(step.descriptionKey)}</p>
            <StepGif step={step}/>
            <div className="help-roadmap-detail-actions">
                <StepActionButton step={step} onNavigate={onNavigate}/>
                <StepDoneAction step={step} toggleManualStep={toggleManualStep}/>
            </div>
            <PowerUpsDropdown step={step}/>
        </div>
    );
}

export function HelpDrawer() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {isOpen, closeDrawer, focusedStepKey} = useHelpDrawer();
    const [openKey, setOpenKey] = useState(null);
    const {steps, completedCount, totalCount, percent, toggleManualStep} = useOnboardingProgress({enabled: isOpen});

    useEffect(() => {
        if (isOpen) {
            setOpenKey(focusedStepKey || null);
        }
    }, [isOpen, focusedStepKey]);

    useEffect(() => {
        if (!isOpen) return;
        const handleEscape = (e) => {
            if (e.key === 'Escape') closeDrawer();
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, closeDrawer]);

    const handleNavigate = (path) => {
        closeDrawer();
        navigate(path);
    };

    const renderedSteps = useMemo(() => steps.map((step, index) => ({
        ...step,
        index: index + 1,
        isOpen: openKey === step.key,
        isActive: !step.completed && steps.findIndex(item => !item.completed) === index,
    })), [steps, openKey]);

    if (!isOpen) return null;

    const content = (
        <>
            <div
                className="help-drawer-overlay"
                onClick={closeDrawer}
                role="presentation"
                aria-hidden="true"
            />
            <aside className="help-drawer" role="dialog" aria-labelledby="help-drawer-title">
                <header className="help-drawer-header">
                    <div className="help-drawer-header-content">
                        <span className="help-drawer-brand-mark" aria-hidden="true"/>
                        <h2 id="help-drawer-title" className="help-drawer-title">
                            {t('help.drawer_title')}
                        </h2>
                    </div>
                    <button
                        type="button"
                        className="help-drawer-close"
                        onClick={closeDrawer}
                        aria-label={t('help.close')}
                    >
                        <CaretDown size={18} weight="bold"/>
                        <X className="help-drawer-close-x" size={16} weight="bold"/>
                    </button>
                </header>

                <div className="help-drawer-body">
                    <div className="help-progress-summary">
                        <div className="help-progress-summary__row">
                            <span className="help-progress-summary__title">{t('help.mini_bar_title')}</span>
                            <span className="help-progress-summary__count">
                                {t('help.mini_bar_steps', {completed: completedCount, total: totalCount})}
                            </span>
                        </div>
                        <div className="help-progress-summary__bar" aria-hidden="true">
                            <span style={{width: `${percent}%`}}/>
                        </div>
                    </div>

                    <ol className="help-roadmap">
                        {renderedSteps.map((step) => (
                            <React.Fragment key={step.key}>
                                {step.sectionKey && (
                                    <li className="help-roadmap-section">
                                        {t(step.sectionKey)}
                                    </li>
                                )}
                                <li className={`help-roadmap-item${step.completed ? ' -completed' : ''}${step.isActive ? ' -active' : ''}${step.isOpen ? ' -open' : ''}`}>
                                    <button
                                        type="button"
                                        className="help-roadmap-step"
                                        onClick={() => setOpenKey(prev => prev === step.key ? null : step.key)}
                                        aria-expanded={step.isOpen}
                                    >
                                        <span className="help-roadmap-step__status">
                                            {step.completed ? <Check size={13} weight="bold"/> : step.index}
                                        </span>
                                        <span className="help-roadmap-step__main">
                                            <span className="help-roadmap-step__title">{t(step.labelKey)}</span>
                                            <PowerUpsIndicator active={step.powerUpsActive} total={step.powerUpsTotal}/>
                                        </span>
                                        <span className="help-roadmap-step__caret" aria-hidden="true">
                                            {step.isOpen ? <CaretDown size={15} weight="bold"/> : <CaretRight size={15} weight="bold"/>}
                                        </span>
                                    </button>

                                    {step.isOpen && (
                                        <StepDetail
                                            step={step}
                                            toggleManualStep={toggleManualStep}
                                            onNavigate={handleNavigate}
                                        />
                                    )}
                                </li>
                            </React.Fragment>
                        ))}
                    </ol>

                    <section className="help-powerups-summary" aria-labelledby="help-powerups-title">
                        <div className="help-powerups-summary__header">
                            <h3 id="help-powerups-title">
                                <span className="help-lightning-icon" aria-hidden="true"/>
                                {t('help.powerups_title')}
                            </h3>
                            <span>{t('help.powerups_activated', {active: POWER_UPS_ACTIVE, total: POWER_UPS_TOTAL})}</span>
                        </div>
                        <div className="help-powerups-summary__bar" aria-hidden="true">
                            <span style={{width: `${(POWER_UPS_ACTIVE / POWER_UPS_TOTAL) * 100}%`}}/>
                        </div>
                        <div className="help-powerups-grid" aria-label={t('help.powerups_title')}>
                            {Array.from({length: POWER_UPS_TOTAL}).map((_, index) => (
                                <span
                                    key={index}
                                    className={`help-powerups-token${index < POWER_UPS_ACTIVE ? ' -active' : ''}`}
                                    aria-hidden="true"
                                />
                            ))}
                        </div>
                        <p>{t('help.powerups_description')}</p>
                    </section>
                </div>
            </aside>
        </>
    );

    return createPortal(content, document.body);
}

export default HelpDrawer;
